import * as Sentry from "@sentry/react";
import { useT } from "@transifex/react";
import React, { useEffect, useMemo } from "react";
import { useHistory } from "react-router-dom";
import CreateInfoModal from "../../../basic/create/InfoModal/CreateInfoModal";
import TestReadyPopup from "../../../complex/anonymousAccountFlow/TestReadyPopup";
import { cp } from "../../../config";
import type { AssistantHandoffState } from "../../../@types/anonymousFlow";
import { ANONYMOUS_FLOW_ANALYTICS_SOURCE } from "../../../constants/AnonymousFlowConstants";
import { useGeneralContext } from "../../../context/AppGeneralContext";
import { useTestCreationContext } from "../../../context/TestCreationContext";
import { clearAnonymousTestHandoff, persistAnonymousTestHandoff } from "../../../helpers/AnonymousTestHandoff";
import { useLogger } from "../../../hooks/useLogger";
import type { SsoProvider } from "../../../constants/SsoConstants";
import { buildSsoRegisterUrl, registerUser, RegistrationOutcomes } from "../../../networking/registration";

export enum SummaryOpenModals {
    DELETION_FORBIDDEN = "DELETION_FORBIDDEN",
    UNUSED_INITIAL_VOCABULARY = "UNUSED_INITIAL_VOCABULARY",
    ANONYMOUS_USER_REGISTRATION = "ANONYMOUS_USER_REGISTRATION",
}

interface SummaryModalsProps {
    openModalType: SummaryOpenModals | undefined;
    onClose: () => void;
    finishTestEditing: () => void;
}

const SummaryModals: React.FC<SummaryModalsProps> = (props) => {
    const { saveTest, testContent, testName, prepareFullTestDetails } = useTestCreationContext();
    const { establishSession } = useGeneralContext();
    const { logEvent } = useLogger();
    const history = useHistory<AssistantHandoffState>();

    const t = useT();

    const t_ok = t("Ok", { _tags: "CreateTest,Summary" });
    const t_canNotDeleteLastExercise = t(
        "This exercise can't be deleted. The test needs to contain at least one exercise.",
        { _tags: "CreateTest,Summary" }
    );
    const t_unusedInitialVocabulary = t(
        "Warning! Some of the cards you've chosen have not been used in any of the exercises. The cards will not be saved for further editing of the exercise. Are you sure you want to continue?",
        { _tags: "CreateTest,Summary" }
    );

    const isAnonymousFlowOpen = props.openModalType === SummaryOpenModals.ANONYMOUS_USER_REGISTRATION;

    // Stable identity while open: PDFDownloadLink re-renders the PDF for every new testDetails object.
    const anonymousTestDetails = useMemo(
        () => (isAnonymousFlowOpen ? prepareFullTestDetails(testContent) : undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [isAnonymousFlowOpen, testContent, testName]
    );

    useEffect(() => {
        if (isAnonymousFlowOpen) {
            logEvent("Anonymous Save Modal Viewed");
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [props.openModalType]);

    const handleAnonymousDismiss = () => {
        // Sweeps up handoffs left behind by older app versions; this popup only writes one on the login path.
        clearAnonymousTestHandoff();
        logEvent("Anonymous Save Modal Dismissed");
        props.onClose();
    };

    const handleRegister = async (email: string) => {
        const registration = await registerUser(email);
        if (registration.outcome !== RegistrationOutcomes.SUCCESS) return registration.outcome;

        // establishSession is what triggers AnonymousTestClaimer, and this path saves for itself: without
        // the clear, a handoff from an earlier abandoned login would be claimed on top of the save below.
        clearAnonymousTestHandoff();

        // The account exists and its JOSSO cookie is already set, but the app itself would stay anonymous, so
        // the teacher is sent to the login instead of a half-logged-in overview. The test is still in memory:
        // the login button's save goes out with that cookie and lands on the new account.
        const userId = await establishSession(registration.sessionId).catch((error) => {
            Sentry.captureException(error, { extra: { stage: "establishSession after registration" } });
            return "";
        });
        if (!userId) return RegistrationOutcomes.SESSION_FAILED;

        logEvent("created account", { source: ANONYMOUS_FLOW_ANALYTICS_SOURCE });

        await saveTest(testContent, true, userId);
        history.push("/assistent", { anonymousAccountCreatedEmail: email });

        return registration.outcome;
    };

    // The redirect unloads the page, so localStorage is the only bridge back to AnonymousTestClaimer, which links the
    // test once the login is done.
    const saveTestAndRedirect = async (
        eventName: string,
        url: string,
        eventProperties: Record<string, string> = {}
    ) => {
        const handoff = await saveTest(testContent, true);

        if (handoff) {
            persistAnonymousTestHandoff(handoff);
        }

        logEvent(eventName, { ...eventProperties, source: ANONYMOUS_FLOW_ANALYTICS_SOURCE });
        window.location.assign(url);
    };

    const handleOpenLogin = () =>
        saveTestAndRedirect(
            "Login Button Clicked",
            `${cp.cfg.REACT_APP_P6_BASE_WEBSITE}classic/service/user/login.html`
        );

    const handleSsoRegister = (provider: SsoProvider) =>
        saveTestAndRedirect("clicked sso button", buildSsoRegisterUrl(provider), { provider });

    return (
        <>
            <CreateInfoModal
                isOpen={props.openModalType === SummaryOpenModals.UNUSED_INITIAL_VOCABULARY}
                onRequestClose={props.onClose}
                continueText={t_ok}
                continueAction={props.finishTestEditing}
                cancelAction={props.onClose}
                compact
            >
                {t_unusedInitialVocabulary}
            </CreateInfoModal>
            <CreateInfoModal
                isOpen={props.openModalType === SummaryOpenModals.DELETION_FORBIDDEN}
                onRequestClose={props.onClose}
                continueText={t_ok}
                continueAction={props.onClose}
                compact
            >
                {t_canNotDeleteLastExercise}
            </CreateInfoModal>
            <TestReadyPopup
                isOpen={isAnonymousFlowOpen}
                onClose={handleAnonymousDismiss}
                testDetails={anonymousTestDetails}
                onRegister={handleRegister}
                onOpenLogin={handleOpenLogin}
                onSsoRegister={handleSsoRegister}
            />
        </>
    );
};

export default SummaryModals;
